import React, { useState, useEffect } from 'react';
import styles from './HUDOverlay.module.css';
import ProjectsModal from './ProjectsModal';
import ResultsModal from './ResultsModal';
import TargetingSystem from './TargetingSystem';

function HUDOverlay({ onDisconnect, onViewProbeResults, onToggleAudio, isAudioMuted, launchStatus, isTargeting, targetingStartTime, isMobile = false }) {
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [coordinates, setCoordinates] = useState({
    x: 0,
    y: 0,
    z: 0
  });
  const [uptime, setUptime] = useState('00:00:00');

  // Simulate realistic orbital coordinates
  useEffect(() => {
    const startTime = Date.now();
    
    // Base orbital parameters for a satellite around Earth-like planet
    const orbitRadius = 6800; // km (similar to ISS altitude + Earth radius)
    const orbitSpeed = 0.0001; // radians per millisecond (about 90 minute orbit)
    const inclination = 0.4; // orbital inclination in radians (~23 degrees)
    
    // Satellite drift parameters (slowly moving up and left as specified)
    const driftSpeedX = -0.002; // km per second westward drift
    const driftSpeedY = 0.001; // km per second upward drift
    
    const updateCoordinates = () => {
      const currentTime = Date.now();
      const elapsed = (currentTime - startTime) / 1000; // seconds elapsed
      
      // Calculate orbital position
      const angle = elapsed * orbitSpeed;
      
      // Base orbital coordinates
      const baseX = Math.cos(angle) * orbitRadius;
      const baseZ = Math.sin(angle) * orbitRadius;
      const baseY = Math.sin(angle * 2) * Math.sin(inclination) * 200; // Orbital inclination effect
      
      // Add drift (satellite slowly moving up and left)
      const driftX = driftSpeedX * elapsed;
      const driftY = driftSpeedY * elapsed;
      
      // Final coordinates with some realistic noise
      const finalX = baseX + driftX + (Math.sin(elapsed * 0.1) * 2);
      const finalY = baseY + driftY + (Math.cos(elapsed * 0.15) * 1.5);
      const finalZ = baseZ + (Math.sin(elapsed * 0.08) * 3);
      
      setCoordinates({
        x: finalX.toFixed(2),
        y: finalY.toFixed(2),
        z: finalZ.toFixed(2)
      });
    };

    // Update coordinates every 100ms for smooth movement
    const interval = setInterval(updateCoordinates, 100);
    
    // Initial update
    updateCoordinates();

    return () => clearInterval(interval);
  }, []);

  // Real uptime timer
  useEffect(() => {
    const startTime = Date.now();
    
    const updateUptime = () => {
      const elapsed = Date.now() - startTime;
      const totalSeconds = Math.floor(elapsed / 1000);
      
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      const formattedUptime = 
        `${hours.toString().padStart(2, '0')}:` +
        `${minutes.toString().padStart(2, '0')}:` +
        `${seconds.toString().padStart(2, '0')}`;
      
      setUptime(formattedUptime);
    };

    // Update uptime every second
    const uptimeInterval = setInterval(updateUptime, 1000);
    
    // Initial update
    updateUptime();

    return () => clearInterval(uptimeInterval);
  }, []);

  return (
    <div className={`${styles.hudOverlay} ${isMobile ? styles.mobileHud : ''}`}>
      {/* Coordinates Display - top-left (hidden on mobile) */}
      {!isMobile && (
        <div className={styles.coords}>
          <div className={styles.coordLine}>X: {coordinates.x} km</div>
          <div className={styles.coordLine}>Y: {coordinates.y} km</div>
          <div className={styles.coordLine}>Z: {coordinates.z} km</div>
        </div>
      )}

      {/* Feed Label - centered at top (hidden on mobile) */}
      {!isMobile && (
        <div className={styles.feedLabel}>
          <span>SATELLITE FEED SPL-001 – CAMERA 4</span>
          <div className={styles.onlineIndicator}></div>
        </div>
      )}

      {/* Control buttons - top-right */}
      <div className={styles.topRightControls}>
        <button
          onClick={onToggleAudio}
          className={`${styles.audioButton} ${isAudioMuted ? styles.audioMuted : ''}`}
          aria-label={isAudioMuted ? "Unmute audio" : "Mute audio"}
        >
          {isAudioMuted ? (
            <svg viewBox="0 0 24 24" className={styles.audioIcon}>
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className={styles.audioIcon}>
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          )}
        </button>
        
        <button
          onClick={onDisconnect}
          className={styles.disconnectButton}
        >
          {isMobile ? 'EXIT' : 'DISCONNECT'}
        </button>
      </div>

      {/* Mission Control Readout - below disconnect button (hidden on mobile) */}
      {!isMobile && (
        <div className={styles.missionControl}>
          <div>ID: SPL-001</div>
          <div>Uptime: {uptime}</div>
          <div>Telemetry: NOMINAL</div>
        </div>
      )}

      {/* HUD Control Buttons - left side */}
      <div className={styles.controls}>
        <button className={styles.hudButton} onClick={() => setProjectsOpen(true)}>Projects</button>
        <button 
          className={`${styles.hudButton} ${resultsOpen ? styles.hudButtonActive : ''}`} 
          onClick={() => setResultsOpen(!resultsOpen)}
        >
          About
        </button>
      </div>

      {/* Targeting System - bottom-right corner */}
      <TargetingSystem 
        isTargeting={isTargeting}
        targetingStartTime={targetingStartTime}
      />

      {/* Status Bar - full-width bar with footer links and status indicators */}
      <div className={styles.statusBar}>
        {/* Footer Links - left side of status bar */}
        <div className={styles.hudFooter}>
          <a 
            href="https://x.com/solportfun" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.hudFooterLink}
            aria-label="Follow us on X"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className={styles.hudFooterIcon}>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <span>@solportfun</span>
          </a>
          
          <div className={styles.hudFooterSeparator}>|</div>
          
          <a 
            href="/docs" 
            className={styles.hudFooterLink}
            aria-label="View Documentation"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className={styles.hudFooterIcon}>
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
            <span>Documentation</span>
          </a>
        </div>

        {/* Status Indicators - right side of status bar */}
        <div className={styles.statusIndicators}>
          <span>SCAN: IDLE</span>
          <span className={launchStatus === 'ACTIVE' ? styles.launchActive : styles.launchStandby}>
            LAUNCH: {launchStatus || 'STANDBY'}
          </span>
        </div>
      </div>

      {/* Modal Components */}
      <ProjectsModal 
        open={projectsOpen} 
        onClose={() => setProjectsOpen(false)}
        onViewProbeResults={onViewProbeResults}
      />
      <ResultsModal open={resultsOpen} onClose={() => setResultsOpen(false)} />
    </div>
  );
}

export default HUDOverlay;
